// components/InlineWidgetTag/index.tsx
import React, { useCallback, useMemo, useState, useRef } from "react";
import { CountWidgetState, IInlineWidgetData, INotesCountWidgetData, IWidgetTagProps, ReferenceDrawerVisibilityType, StudyType, WidgetType } from "../../models/interfaces";
import "./style.css";
import { ActionTypes, IResponse, ModuleTypes } from "src/pages/Studies/models/interfaces";
import { getWidgetData } from "../../serviceHelper";
import { useDispatch, useSelector } from "react-redux";
import {
	setCountError,
	setCountWidgetOpen,
	setCountWidgetLoading,
	setCountWidgetData,
	setCountSelectedQuote,
	setCountSelectedWidgetData,
	setCountLabels,
	setCountWidgetSearchPhrase,
	setCountSelectedType,
	setCountSelectedStudies,
	setCountSelectedFileData,
	setCountSelectedMetaData,
	setCountSelectedMetaDataLabels,
	setNotesCountWidgetData,
} from "../../states/countWidgetSlice";
import { RootState } from "src/store";
import { resetActiveCitationReferences, setActiveWidgetData, setIsAskKuralSidebarOpen, setReferenceDrawerVisibility, setAgenticWidgetMetaData } from "../../states/askKuralSlice";
import { Popover } from "src/assets/imports/antdComponents";
import AgenticInlineWidgetPopover from "../AgenticInlineWidgetPopover";
import { useActionTracking } from "src/components/UserMonitoring/useActionTracking";
import { convertTagsToOptions, convertToSelectedTagDataFormat } from "../../transformer";
import TagLoader from "public/icon/TagLoader.svg";
import { useCitationLoaderState } from "../../hooks/useCitationLoaderState";
import { countColors as colors, countsColorMap as colorMap } from "src/pages/Insights/models/interfaces";
import { assignColorToTag } from "src/pages/Insights/transformer";

const NOTES_COUNTS_WIDGET_TYPES = new Set(["notes-counts", "notes_counts"]);
const isNotesCountsWidget = (widgetTypeValue?: string | null): boolean =>
	!!widgetTypeValue && NOTES_COUNTS_WIDGET_TYPES.has(widgetTypeValue);

interface IWidgetTagProps {
	widgetRef: string;
	widgetType: string;
	studyType: string;
	messageId: string;
	references?: any;
	handleInlineWidgetScroll: (scrollId: string) => void;
	widgetData: IInlineWidgetData[] | null;
	widgetId?: string;
	isReferenceTerminal?: boolean;
	/** Per-message completion override for the citation loader (see useCitationLoaderState). */
	isProcessComplete?: boolean;
}
const InlineWidgetTag: React.FC<IWidgetTagProps> = ({ widgetRef, widgetId, widgetType, studyType, messageId, references, handleInlineWidgetScroll, widgetData, isReferenceTerminal = false, isProcessComplete }) => {
	const dispatch = useDispatch();
	const { activeWidgetData } = useSelector((state: RootState) => state.askKuralSlice);

	const [popoverOpen, setPopoverOpen] = useState(false);
	const [popoverKey, setPopoverKey] = useState(0);

	const primaryWidgetId = widgetData?.[0]?.widgetId;
	const scrollUniqueIdRef = useRef(primaryWidgetId ? `${primaryWidgetId}-${crypto.randomUUID()}` : crypto.randomUUID());
	const scrollUniqueId = scrollUniqueIdRef.current;
	const [labelColorMap, setLabelColorMap] = useState<Record<string, Record<string, string>>>({});

	const { trackAction } = useActionTracking(ModuleTypes.AskKural);

	const isSelected = useMemo(() => {
		if (!widgetData || !activeWidgetData?.widgetId) return false;
		return widgetData.some((w) => w.widgetId === activeWidgetData.widgetId);
	}, [activeWidgetData, widgetData]);

	// Determine display label based on widget type
	const displayLabel = useMemo(() => {
		switch (widgetType) {
			case "counts":
				return "Count Widget";
			case "deepdive":
				return "Count Widget";
			case "quant":
				return "Quant Widget";
			case "chart":
				return "Chart";
			default:
				return "Widget";
		}
	}, [widgetType]);

	const hasWidgetData = widgetData !== null && Array.isArray(widgetData) && widgetData.length > 0;
	// Determine if we should show loading state (shared across all inline citation tags).
	const shouldShowLoader = useCitationLoaderState({
		hasData: hasWidgetData,
		isReferenceTerminal,
		isProcessComplete,
	});

	const fetchWidgetData = useCallback(
		async (widgetId: string, effectiveWidgetType: string = widgetType) => {
			try {
				dispatch(setCountWidgetLoading(true));
				const res: IResponse = await getWidgetData({ reference_id: widgetId });
				if (res?.data) {
					const countWidgetData = res.data;
					dispatch(setCountWidgetLoading(false));
					dispatch(setCountWidgetSearchPhrase(countWidgetData?.reworded_query || ""));

					if (effectiveWidgetType === WidgetType.NotesCounts) {
						const notesPayload: INotesCountWidgetData = {
							denominator: countWidgetData?.denominator ?? 0,
							reworded_query: countWidgetData?.reworded_query ?? "",
							study_id: countWidgetData?.study_id ?? "",
							study_name: countWidgetData?.study_name ?? "",
							labels: Array.isArray(countWidgetData?.labels) ? countWidgetData.labels : [],
							references: Array.isArray(countWidgetData?.references) ? countWidgetData.references : [],
							info_message: countWidgetData?.info_message,
							relevant_metadata: Array.isArray(countWidgetData?.relevant_metadata) ? countWidgetData.relevant_metadata : [],
							relevant_metadata_rationale: countWidgetData?.relevant_metadata_rationale || "",
							themes_generated: !!countWidgetData?.themes_generated,
							label_descriptions:
								countWidgetData?.label_descriptions && typeof countWidgetData.label_descriptions === "object"
									? countWidgetData.label_descriptions
									: {},
							is_mutually_exclusive: !!countWidgetData?.is_mutually_exclusive,
							selected_metadata_filters: Array.isArray(countWidgetData?.selected_metadata_filters)
								? countWidgetData.selected_metadata_filters
								: null,
						};
						dispatch(setNotesCountWidgetData(notesPayload));
						dispatch(
							setCountSelectedWidgetData({
								type: WidgetType.NotesCounts,
								widget_id: widgetId,
								widget_state: CountWidgetState.Active,
								study_name: notesPayload.study_name,
								reworded_query: notesPayload.reworded_query,
								study_type: StudyType.Notes,
								denominator: notesPayload.denominator,
								agenticLabelColorMap: labelColorMap[widgetId],
							}),
						);
						dispatch(setCountSelectedType([StudyType.Notes]));
						dispatch(setCountLabels(notesPayload.labels));
						trackAction(ActionTypes.REFLECTIVE_COUNTS_DRAWER_VIEWED, { feature: true, type: undefined });
						return;
					}

					dispatch(
						setCountWidgetData({
							numerator: countWidgetData?.numerator,
							denominator: countWidgetData?.denominator,
							references: countWidgetData?.references,
							transcripts_count: countWidgetData?.transcripts_count,
							studies_count: countWidgetData?.studies_count,
							quotes_count: countWidgetData?.quotes_count,
							custom_segments_saved: !!countWidgetData?.custom_segments_saved,
							custom_segment_tag_category: countWidgetData?.custom_segment_tag_category,
						}),
					);
					dispatch(
						setCountSelectedWidgetData({
							numerator: countWidgetData?.numerator,
							denominator: countWidgetData?.denominator,
							references: countWidgetData?.references,
							transcripts_count: countWidgetData?.transcripts_count,
							studies_count: countWidgetData?.studies_count,
							quotes_count: countWidgetData?.quotes_count,
							type: effectiveWidgetType,
							widget_id: widgetId,
							widget_state: CountWidgetState.Active,
							study_name: countWidgetData?.study_name,
							reworded_query: countWidgetData?.reworded_query,
							study_type: countWidgetData?.payload?.filters?.search_type,
							agenticLabelColorMap: labelColorMap[widgetId], //this param is used to match the label order in counts widget
							custom_segments_saved: !!countWidgetData?.custom_segments_saved,
							custom_segment_tag_category: countWidgetData?.custom_segment_tag_category,
						}),
					);
					const filters = countWidgetData?.filters;
					const selectedStudies = filters?.entity_id ? (Array.isArray(filters.entity_id) ? filters.entity_id : [filters.entity_id]) : [];
					const selectedFileData = filters?.file_ids ? (Array.isArray(filters.file_ids) ? filters.file_ids : [filters.file_ids]) : [];
					dispatch(setCountSelectedStudies(selectedStudies));
					dispatch(setCountSelectedFileData(selectedFileData));
					dispatch(setCountSelectedType([StudyType.Transcripts]));
					const labels = countWidgetData?.labels;
					let labelArray: string[] = [];
					if (Array.isArray(labels)) {
						labelArray = labels;
					} else if (labels && typeof labels === "object") {
						labelArray = Object.keys(labels);
					}
					dispatch(setCountLabels(labelArray));
					const metaData = countWidgetData?.metadata ? convertTagsToOptions(countWidgetData?.metadata) : [];
					dispatch(setAgenticWidgetMetaData(metaData));
					//select the metadata based on the receieved data
					if (countWidgetData?.selected_metadata_filters) {
						const selectedMetaData = convertToSelectedTagDataFormat(countWidgetData?.selected_metadata_filters);
						const selectedMetaValue = selectedMetaData?.map((item) => item?.value);
						dispatch(setCountSelectedMetaData(selectedMetaValue));
						dispatch(setCountSelectedMetaDataLabels(selectedMetaData));
					} else {
						//for older chats when we dont receieve the selected meta data, check the s_filters_selected and based on that select all recieved filter data
						if (countWidgetData?.is_filters_selected) {
							const selectedMetaData = convertToSelectedTagDataFormat(countWidgetData?.metadata);
							const selectedMetaValue = selectedMetaData?.map((item) => item?.value);
							dispatch(setCountSelectedMetaData(selectedMetaValue));
							dispatch(setCountSelectedMetaDataLabels(selectedMetaData));
						}
					}
					trackAction(ActionTypes.REFLECTIVE_COUNTS_DRAWER_VIEWED, { feature: true, type: undefined });
				}
			} catch (error) {
				console.error("Error fetching widget data:", error);
				dispatch(setCountWidgetLoading(false));
				dispatch(setCountError(true));
			}
		},
		[labelColorMap],
	);

	const handleClick = useCallback(async () => {
		if (!widgetId || activeWidgetData?.widgetId === widgetId) return;
		const primaryWidget = widgetData?.find((w) => w.widgetId === widgetId) || widgetData?.[0];
		const effectiveWidgetType = isNotesCountsWidget(primaryWidget?.widget_type) ? WidgetType.NotesCounts : widgetType;

		handleInlineWidgetScroll(scrollUniqueId);
		dispatch(
			setActiveWidgetData({
				widgetId: widgetId,
				studyType: studyType,
				messageId: messageId,
				widgetData: references,
			}),
		);
		dispatch(setCountWidgetOpen(true));
		dispatch(
			setCountSelectedWidgetData({
				type: effectiveWidgetType,
				widget_id: widgetId,
				widget_state: CountWidgetState.Active,
			}),
		);
		dispatch(setIsAskKuralSidebarOpen(false));
		dispatch(resetActiveCitationReferences());
		dispatch(setReferenceDrawerVisibility(ReferenceDrawerVisibilityType.hidden));
		await fetchWidgetData(widgetId, effectiveWidgetType);
	}, [widgetId, studyType, messageId, references, activeWidgetData, scrollUniqueId, widgetData, widgetType, fetchWidgetData]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Enter") {
				e.preventDefault();
				handleClick();
			}
		},
		[handleClick],
	);

	const handleViewDetails = useCallback(
		async (selectedRef: IInlineWidgetData) => {
			setPopoverOpen(false);
			if (!selectedRef?.widgetId) return;
			const effectiveWidgetType = isNotesCountsWidget(selectedRef?.widget_type) ? WidgetType.NotesCounts : widgetType;

			handleInlineWidgetScroll(scrollUniqueId);
			dispatch(
				setActiveWidgetData({
					widgetId: selectedRef.widgetId,
					studyType,
					messageId,
					widgetData: references,
				}),
			);
			dispatch(setCountWidgetOpen(true));
			dispatch(
				setCountSelectedWidgetData({
					type: effectiveWidgetType,
					widget_id: selectedRef?.widgetId,
					widget_state: CountWidgetState.Active,
				}),
			);
			dispatch(setIsAskKuralSidebarOpen(false));
			dispatch(resetActiveCitationReferences());
			dispatch(setReferenceDrawerVisibility(ReferenceDrawerVisibilityType.hidden));

			await fetchWidgetData(selectedRef.widgetId, effectiveWidgetType);
		},
		[fetchWidgetData, studyType, messageId, references, scrollUniqueId, widgetType],
	);

	const convertTopLabelsToDisplayFormat = useCallback((widgetItem: IInlineWidgetData) => {
		if (!widgetItem?.tool_tip) return { convertedLabels: [], widgetLabelColorMap: {} };
		const { labels, denominator } = widgetItem?.tool_tip;

		const allLabels = labels?.map((item: any) => item?.name);

		const maxCount = Math.max(...labels?.map((l: any) => l?.count));
		const noMentionCount = denominator - maxCount;

		if (noMentionCount > 0) {
			allLabels?.push("No Mention");
		}

		const widgetLabelColorMap: Record<string, string> = {};
		const currentlyUsedColors: string[] = [];

		if (allLabels?.includes("No Mention")) {
			widgetLabelColorMap["No Mention"] = "bg-gray-200";
			currentlyUsedColors?.push("bg-gray-200");
		}

		allLabels?.forEach((label, index) => {
			if (label !== "No Mention" && !widgetLabelColorMap[label]) {
				const availableColorsForOthers = colors.filter((color) => color !== "bg-gray-200");

				const colorAssignment = assignColorToTag(label, widgetLabelColorMap, currentlyUsedColors, availableColorsForOthers, index);

				Object.assign(widgetLabelColorMap, colorAssignment);

				if (colorAssignment[label]) {
					currentlyUsedColors.push(colorAssignment[label]);
				}
			}
		});

		const convertedLabels = labels?.map((item: any) => ({
			label: item?.name,
			count: item?.count,
			percentage: denominator > 0 ? Math.round((item?.count / denominator) * 100) : 0,
			color: widgetLabelColorMap[item?.name] || "#cccccc",
			hexColor: colorMap[widgetLabelColorMap[item?.name]] || "#cccccc",
		}));

		convertedLabels.sort((a, b) => {
			if (a.label === "No Mention") return 1;
			if (b.label === "No Mention") return -1;
			return 0;
		});

		return { convertedLabels, widgetLabelColorMap };
	}, []);

	const TopDataIntoChartFormat = useMemo(() => {
		if (!widgetData || widgetData?.length === 0) return {};

		const chartDataMap: Record<string, any[]> = {};
		const colorMapData: Record<string, Record<string, string>> = {};

		widgetData.forEach((widgetItem) => {
			if (widgetItem?.widgetId) {
				const { convertedLabels, widgetLabelColorMap } = convertTopLabelsToDisplayFormat(widgetItem);
				chartDataMap[widgetItem?.widgetId] = convertedLabels || [];
				colorMapData[widgetItem?.widgetId] = widgetLabelColorMap;
			}
		});

		setLabelColorMap(colorMapData);
		return chartDataMap;
	}, [widgetData]);

	// Handle loading / empty states when no widget data
	if (widgetData === null || (Array.isArray(widgetData) && widgetData?.length === 0)) {
		if (!shouldShowLoader) {
			// Render nothing when terminal with no widget data (no error pill).
			return null;
		}
		return (
			<span className="inline-widget-tag-wrapper inline-flex items-center inline-widget-loading">
				<TagLoader className="spinning-loader" />
			</span>
		);
	}

	return (
		<Popover
			open={popoverOpen && widgetData !== null}
			trigger="hover"
			placement="bottomLeft"
			onOpenChange={(vis) => {
				setPopoverOpen(vis);
				if (vis) {
					setPopoverKey((prev) => prev + 1);
				}
			}}
			rootClassName="rounded-xl citation-popover"
			overlayInnerStyle={{ padding: 0 }}
			mouseEnterDelay={0.3}
			mouseLeaveDelay={0.1}
			getPopupContainer={() => document.body}
			content={<AgenticInlineWidgetPopover key={popoverKey} widgetData={widgetData} onViewDetails={handleViewDetails} TopDataIntoChartFormat={TopDataIntoChartFormat} />}>
			<span
				id={scrollUniqueId}
				className={`inline-widget-tag ${isSelected ? "selected" : ""}`}
				onClick={handleClick}
				onKeyDown={handleKeyDown}
				role="button"
				tabIndex={0}
				aria-label={`View ${displayLabel}`}>
				<span className="default-widget-icon">
					<i className="material-symbols-outlined text-base mt-1">bar_chart_4_bars</i>
				</span>
				{widgetData && widgetData.length > 1 && <span>({widgetData.length})</span>}
				<i className="material-symbols-outlined text-base">chevron_right</i>
			</span>
		</Popover>
	);
};

export default InlineWidgetTag;
